import React from 'react';

export type ToastType = 'info' | 'success' | 'warning' | 'error';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastMessage {
  id: string;
  type: ToastType;
  message: React.ReactNode;
  actions?: ToastAction[];
  timeout?: number;
  /** Number of merged duplicates; rendered as a count badge. Unset/1 for a single toast. */
  count?: number;
  /** Explicit dedup key. When set, used instead of the default `${type} ${message}` key. */
  dedupeId?: string;
  /** Opaque passthrough used by the legacy adapter to round-trip Fluent MessageBar props.
   *  TODO: remove once the legacy UI will be decommissioned and not used in new UI
   */
  meta?: unknown;
}

type AddToastInput = {
  type: ToastType;
  message: React.ReactNode;
  actions?: ToastAction[];
  timeout?: number;
  /** When provided and a toast with this id exists, its content is replaced (upsert). */
  id?: string;
  /** Explicit dedup key; overrides the default content-based key. */
  dedupeId?: string;
  meta?: unknown;
};

interface ToastContextValue {
  toasts: ToastMessage[];
  /** Adds (or upserts/dedups) a toast and returns its id. */
  addToast: (input: AddToastInput) => string;
  removeToast: (id: string) => void;
  removeAllToasts: () => void;
}

let toastIdCounter = 0;
const generateId = () => `toast-${++toastIdCounter}-${Date.now()}`;

// The dedup key is the explicit `dedupeId`, else `${type} ${message}` — but only when the message
// is a plain string; arbitrary ReactNode messages have no stable, comparable key, so they are
// never merged unless an explicit `dedupeId` is provided.
const toastKey = (t: Pick<ToastMessage, 'type' | 'message' | 'dedupeId'>): string | undefined =>
  t.dedupeId ?? (typeof t.message === `string` ? `${t.type} ${t.message}` : undefined);

const ToastContext = React.createContext<ToastContextValue | undefined>(undefined);

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toasts, setToasts] = React.useState<ToastMessage[]>([]);
  // `toastsRef` mirrors the latest toasts so the stable `addToast` can read current state
  // *synchronously* — both to return the correct id (the React state updater runs later, during
  // render, so the return value cannot depend on it) and to dedup across same-tick calls,
  // regardless of where `addToast` was captured (e.g. once by the QueryClient error handlers).
  // Every mutation writes the ref before `setToasts`; the render below re-syncs it defensively.
  const toastsRef = React.useRef(toasts);
  React.useEffect(() => {
    toastsRef.current = toasts;
  }, [toasts]);
  // `addToast` is intentionally stable (no deps): identity must not change across renders, or
  // consumers that capture it once would hold a stale closure.
  const addToast = React.useCallback((input: AddToastInput): string => {
    const prev = toastsRef.current;
    let id: string;
    let next: ToastMessage[];

    if (input.id !== undefined) {
      // 1. Explicit id → upsert: replace the matching toast's content in place (count untouched).
      id = input.id;
      const existingIdx = prev.findIndex((t) => t.id === id);
      next =
        existingIdx === -1
          ? [...prev, { ...input, id }]
          : prev.toSpliced(existingIdx, 1, { ...prev[existingIdx], ...input, id });
    } else {
      // 2. Content dedup: bump the count of an existing toast that shares the same key.
      const dedupeKey = toastKey(input);
      const existingIdx = dedupeKey === undefined ? -1 : prev.findIndex((t) => toastKey(t) === dedupeKey);
      if (existingIdx === -1) {
        // 3. New toast.
        id = generateId();
        next = [...prev, { ...input, id }];
      } else {
        const existingItem = prev[existingIdx];
        id = existingItem.id;
        next = prev.toSpliced(existingIdx, 1, { ...existingItem, ...input, id, count: (existingItem.count || 1) + 1 });
      }
    }

    toastsRef.current = next;
    setToasts(next);
    return id;
  }, []);

  const removeToast = React.useCallback((id: string) => {
    const next = toastsRef.current.filter((t) => t.id !== id);
    toastsRef.current = next;
    setToasts(next);
  }, []);

  const removeAllToasts = React.useCallback(() => {
    toastsRef.current = [];
    setToasts([]);
  }, []);

  const value = React.useMemo(
    () => ({ toasts, addToast, removeToast, removeAllToasts }),
    [toasts, addToast, removeToast, removeAllToasts]
  );

  return React.createElement(ToastContext.Provider, { value }, children);
};

export const useToast = () => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

/**
 * Non-throwing accessor returning `undefined` when no `ToastProvider` is mounted. Intended for the
 * legacy `useToast` adapter, which historically used a default (no-op) context and must not throw
 * when rendered outside a provider (e.g. isolated unit tests / Storybook). Prefer `useToast` in new
 * code so missing-provider mistakes surface loudly.
 * TODO: remove once the legacy UI will be decommissioned.
 */
export const useOptionalToast = (): ToastContextValue | undefined => React.useContext(ToastContext);
